/**
 * GPU texture-capability helpers.
 *
 * Resolves the device's max anisotropic filtering level once at renderer
 * creation so texture factories throughout the viewport can apply the same
 * filter quality without re-querying the GPU on every allocation.
 *
 * Material lifetime is owned by the consumer that allocated the material
 * (see SceneRenderer / PrimitiveRenderer / MannequinRenderer / the factories
 * in createViewportMaterial.ts and createPaintOverlayMaterial.ts). There is
 * no shared material cache — every call to a factory allocates a fresh
 * material and the caller pairs it with a useEffect cleanup that disposes
 * it on memo replay or unmount.
 */

import type * as THREE from "three/webgpu";

// ---------------------------------------------------------------------------
// Max anisotropy — resolved once from the GPU, shared by all texture code
// ---------------------------------------------------------------------------

let _maxAnisotropy = 1;

/** Resolve the GPU's max anisotropic filtering level. Safe to call repeatedly. */
export function initMaxAnisotropy(gl: THREE.WebGPURenderer): void {
  if (_maxAnisotropy > 1) return;
  // WebGPURenderer exposes capabilities with the same API; fall back to 16 if unavailable
  try {
    _maxAnisotropy = (gl as any).capabilities?.getMaxAnisotropy?.() ?? 16;
  } catch {
    _maxAnisotropy = 16;
  }
}

/** Current max anisotropy level (1 until {@link initMaxAnisotropy} is called). */
export function getMaxAnisotropy(): number {
  return _maxAnisotropy;
}
